import { useTranslation } from 'react-i18next';
import { CalendarCheck, Check, CreditCard, Globe, Minus, Shield, X, type LucideIcon } from 'lucide-react';
import { LandingLayout } from './shared/LandingLayout';
import { SEOHead } from './shared/SEOHead';
import type { ComparisonValue, LandingConfig } from './types';
import { useLandingAnalytics } from '@/hooks/useLandingAnalytics';
import { useDetectedCurrency } from '@/hooks/useCurrency';
import { cn } from '@/lib/utils';
import { LandingFaq } from './invite-shared/LandingFaq';
import { PricingBand, TrialButtons } from './invite-shared/TrialCta';
import {
  Eyebrow,
  IconTile,
  Section,
  SectionTitle,
  cardClass,
  landingGradientStyles,
  type FaqItem,
  type Item,
} from './invite-shared/layout';

const SITE = 'https://slotyapp.com';
const TRUST_ICONS: LucideIcon[] = [CreditCard, Shield, Globe];

/**
 * Single rendering path for every industry-vertical landing page (/for-salons,
 * /for-doctors, …), on the Sloty design system like /for-trainers and
 * /for-small-gyms. Per-vertical TSX files are shims that hand a config to this
 * component; section order and styling are owned here.
 */
export function LandingPage({ config }: { config: LandingConfig }) {
  const ns = config.namespace;
  const { t } = useTranslation([ns, 'landing-common', 'common']);
  useLandingAnalytics(config.analyticsId);
  const { getStartingPrice } = useDetectedCurrency();

  const seoKey = `common:seo.${camelCase(config.analyticsId)}`;
  const title = t(`${seoKey}.title`);
  const description = t(`${seoKey}.description`);
  const url = `${SITE}${config.canonicalPath}`;
  const faq = (t(`${ns}:faq.items`, { returnObjects: true }) as FaqItem[]).map((f) => ({
    ...f,
    answer: f.answer.replace('{{startingPrice}}', getStartingPrice()),
  }));

  return (
    <>
      <SEOHead
        title={title}
        description={description}
        canonical={url}
        ogImage={`${SITE}${config.ogImagePath}`}
        structuredData={[
          { '@context': 'https://schema.org', '@type': 'WebPage', name: title, description, url },
          {
            '@context': 'https://schema.org',
            '@type': 'FAQPage',
            mainEntity: faq.map((f) => ({ '@type': 'Question', name: f.question, acceptedAnswer: { '@type': 'Answer', text: f.answer } })),
          },
        ]}
      />
      <LandingLayout>
        <div className={`lp-${config.slug}`}>
          <style>{landingGradientStyles}</style>
          <Hero config={config} />
          <BeforeAfter ns={ns} />
          <Features config={config} />
          <Comparison config={config} />
          <PricingBand
            className="bg-surface"
            eyebrow={t('landing-common:vertical.pricing.eyebrow')}
            title={t('landing-common:vertical.pricing.title')}
            subtitle={t('landing-common:vertical.pricing.subtitle', { startingPrice: getStartingPrice() })}
            cta={t('landing-common:vertical.pricing.cta')}
          />
          <LandingFaq ns={ns} />
          <FinalCta ns={ns} />
        </div>
      </LandingLayout>
    </>
  );
}

function Hero({ config }: { config: LandingConfig }) {
  const { t } = useTranslation([config.namespace, 'landing-common']);
  const ns = config.namespace;
  const trust = t('landing-common:vertical.trust', { returnObjects: true }) as string[];
  const base = config.heroImage;
  return (
    <section className="lp-hero-fade px-5 pb-14 pt-8 sm:px-8 lg:px-20 lg:pb-[104px] lg:pt-[72px]">
      <div className="mx-auto grid w-full max-w-[1280px] items-center gap-10 lg:grid-cols-12 lg:gap-6">
        <div className="flex flex-col gap-5 lg:col-span-6 lg:gap-6">
          <Eyebrow>{t(`${ns}:hero.eyebrow`)}</Eyebrow>
          <h1 className="m-0 text-balance font-display text-[36px] font-semibold leading-[1.1] tracking-[-0.025em] text-ink lg:text-[56px]">
            {t(`${ns}:hero.title`)}
          </h1>
          <p className="m-0 max-w-[540px] text-base leading-[1.6] text-soft lg:text-lg">{t(`${ns}:hero.subtitle`)}</p>
          <div className="flex flex-col gap-3">
            <TrialButtons cta={t('landing-common:vertical.cta')} secondary={t('landing-common:vertical.secondary')} />
            <p className="m-0 text-[13px] text-soft">{t('landing-common:vertical.note')}</p>
          </div>
          <ul className="m-0 flex list-none flex-col gap-3 p-0 text-[13px] font-medium text-soft sm:flex-row sm:flex-wrap sm:gap-x-6">
            {trust.map((item, i) => {
              const Icon = TRUST_ICONS[i];
              return (
                <li key={item} className="flex items-center gap-2">
                  <Icon className="h-4 w-4 shrink-0 text-brand-500" strokeWidth={1.75} aria-hidden="true" />
                  <span>{item}</span>
                </li>
              );
            })}
          </ul>
        </div>
        <div className="relative lg:col-span-5 lg:col-start-8">
          <div className="overflow-hidden rounded-4xl shadow-hero">
            <img
              src={`${base}.jpg`}
              srcSet={`${base}-480w.jpg 480w, ${base}-800w.jpg 800w, ${base}-1200w.jpg 1200w`}
              sizes="(min-width: 1024px) 480px, 100vw"
              alt={t(`${ns}:hero.imageAlt`)}
              width={1536}
              height={1024}
              fetchPriority="high"
              className="block aspect-[4/3] h-auto w-full object-cover lg:aspect-[5/4]"
            />
          </div>
          <BookingCard ns={ns} />
        </div>
      </div>
    </section>
  );
}

/** Small "new booking" card over the hero photo: what the owner sees when a client books. */
function BookingCard({ ns }: { ns: string }) {
  const { t } = useTranslation([ns, 'landing-common']);
  return (
    <div
      className={cn(cardClass, 'absolute -bottom-6 left-4 right-4 flex items-center gap-3.5 p-4 sm:left-auto sm:right-6 sm:w-[300px] lg:-left-10 lg:right-auto')}
      aria-hidden="true"
    >
      <span className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-[14px] bg-success-soft">
        <CalendarCheck className="h-5 w-5 text-success-text" strokeWidth={1.75} />
      </span>
      <div className="flex min-w-0 flex-col">
        <span className="text-[11px] font-bold uppercase tracking-[0.06em] text-success-text">{t('landing-common:vertical.newBooking')}</span>
        <span className="truncate text-[15px] font-semibold text-ink">{t(`${ns}:hero.mock.service`)}</span>
        <span className="truncate text-[13px] text-soft">{t(`${ns}:hero.mock.when`)}</span>
      </div>
    </div>
  );
}

function Mark({ tone }: { tone: 'yes' | 'no' }) {
  return tone === 'yes' ? (
    <span className="mt-0.5 inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-success-soft text-success-text">
      <Check className="h-3.5 w-3.5" strokeWidth={3} aria-hidden="true" />
    </span>
  ) : (
    <span className="mt-0.5 inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-danger-soft text-danger-text">
      <X className="h-3.5 w-3.5" strokeWidth={3} aria-hidden="true" />
    </span>
  );
}

function BeforeAfter({ ns }: { ns: string }) {
  const { t } = useTranslation([ns, 'landing-common']);
  const before = t(`${ns}:beforeAfter.beforeItems`, { returnObjects: true }) as string[];
  const after = t(`${ns}:beforeAfter.afterItems`, { returnObjects: true }) as string[];
  const list = (items: string[], tone: 'yes' | 'no') => (
    <ul className="m-0 flex list-none flex-col gap-3.5 p-0">
      {items.map((item) => (
        <li key={item} className="flex items-start gap-3 text-base leading-[1.5] text-ink lg:text-[17px]">
          <Mark tone={tone} />
          <span>{item}</span>
        </li>
      ))}
    </ul>
  );
  return (
    <Section className="bg-surface pt-20 lg:pt-28">
      <SectionTitle className="mb-10 max-w-[820px] lg:mx-auto lg:mb-14 lg:text-center">{t(`${ns}:beforeAfter.title`)}</SectionTitle>
      <div className="grid gap-4 md:grid-cols-2 lg:gap-6">
        <div className="flex flex-col gap-5 rounded-4xl border border-line bg-surface p-6 lg:p-9">
          <h3 className="m-0 font-display text-xl font-semibold text-soft">{t('landing-common:vertical.before')}</h3>
          {list(before, 'no')}
        </div>
        <div className="flex flex-col gap-5 rounded-4xl border border-brand-200 bg-brand-50 p-6 lg:p-9">
          <h3 className="m-0 font-display text-xl font-semibold text-brand-700">{t('landing-common:vertical.after')}</h3>
          {list(after, 'yes')}
        </div>
      </div>
    </Section>
  );
}

function Features({ config }: { config: LandingConfig }) {
  const ns = config.namespace;
  const { t } = useTranslation(ns);
  const items = t('features.items', { returnObjects: true }) as Item[];
  return (
    <Section id="features" className="bg-brand-50">
      <div className="mb-10 flex flex-col gap-4 lg:mb-14 lg:items-center lg:text-center">
        <SectionTitle className="max-w-[820px]">{t('features.title')}</SectionTitle>
        <p className="m-0 max-w-[680px] text-base leading-[1.6] text-soft lg:text-lg">{t('features.subtitle')}</p>
      </div>
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3 lg:gap-6">
        {items.map((item, i) => (
          <div key={item.title} className={cn(cardClass, 'flex flex-col gap-4 p-6 lg:p-8')}>
            <IconTile icon={config.featureIcons[i]} size="lg" />
            <h3 className="m-0 font-display text-[21px] font-semibold tracking-[-0.02em] text-ink">{item.title}</h3>
            <p className="m-0 text-[15px] leading-[1.6] text-soft lg:text-base">{item.description}</p>
          </div>
        ))}
      </div>
    </Section>
  );
}

function ComparisonMark({ value }: { value: ComparisonValue }) {
  const { t } = useTranslation('landing-common');
  const mark = {
    y: { Icon: Check, cls: 'bg-success-soft text-success-text', label: t('vertical.yes') },
    n: { Icon: X, cls: 'bg-surface-2 text-text-muted', label: t('vertical.no') },
    p: { Icon: Minus, cls: 'bg-warn-soft text-warn-text', label: t('vertical.partly') },
  }[value];
  return (
    <span className={cn('inline-flex h-7 w-7 items-center justify-center rounded-full', mark.cls)} role="img" aria-label={mark.label}>
      <mark.Icon className="h-4 w-4" strokeWidth={2.75} aria-hidden="true" />
    </span>
  );
}

function Comparison({ config }: { config: LandingConfig }) {
  const { t } = useTranslation(config.namespace);
  const columns = t('comparison.columns', { returnObjects: true }) as string[];
  const rows = t('comparison.rows', { returnObjects: true }) as string[];
  const last = columns.length - 1;
  return (
    <Section className="bg-bg-public">
      <SectionTitle className="mb-10 lg:mb-12 lg:text-center">{t('comparison.title')}</SectionTitle>

      {/* Desktop: table, the Sloty column highlighted. */}
      <div className={cn(cardClass, 'mx-auto hidden max-w-[980px] overflow-hidden md:block')}>
        <table className="w-full border-collapse text-left">
          <thead>
            <tr className="border-b border-line">
              <th scope="col" className="px-7 py-4"><span className="sr-only">{t('comparison.title')}</span></th>
              {columns.map((c, i) => (
                <th key={c} scope="col" className={cn('px-4 py-4 text-center text-[15px] font-semibold', i === last ? 'bg-brand-50 text-brand-700' : 'text-soft')}>
                  {c}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, r) => (
              <tr key={row} className="border-b border-line last:border-0">
                <th scope="row" className="px-7 py-4 text-base font-normal text-ink">{row}</th>
                {config.comparison[r].map((v, i) => (
                  <td key={i} className={cn('px-4 py-4 text-center', i === last && 'bg-brand-50')}>
                    <ComparisonMark value={v} />
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Phones: one card per row. */}
      <ul className="m-0 flex list-none flex-col gap-3 p-0 md:hidden">
        {rows.map((row, r) => (
          <li key={row} className="flex flex-col gap-4 rounded-2xl border border-line bg-surface p-4">
            <span className="text-[15.5px] font-semibold text-ink">{row}</span>
            <div className="grid grid-cols-3 gap-2">
              {config.comparison[r].map((v, i) => (
                <div key={i} className="flex flex-col items-center gap-1.5 text-center">
                  <ComparisonMark value={v} />
                  <span className={cn('text-[11.5px] font-semibold', i === last ? 'text-brand-700' : 'text-soft')}>{columns[i]}</span>
                </div>
              ))}
            </div>
          </li>
        ))}
      </ul>
    </Section>
  );
}

function FinalCta({ ns }: { ns: string }) {
  const { t } = useTranslation([ns, 'landing-common']);
  return (
    <Section className="bg-bg-public">
      <div className="lp-final-panel flex flex-col items-stretch gap-8 rounded-[28px] px-[22px] py-8 lg:items-center lg:p-16">
        <div className="flex flex-col gap-4 lg:items-center lg:text-center">
          <SectionTitle>{t(`${ns}:final.title`)}</SectionTitle>
          <p className="m-0 max-w-[560px] text-base leading-[1.6] text-soft lg:text-lg">{t(`${ns}:final.subtitle`)}</p>
        </div>
        <div className="flex flex-col gap-3 lg:items-center">
          <TrialButtons cta={t('landing-common:vertical.cta')} />
          <p className="m-0 text-[13px] text-soft">{t('landing-common:vertical.note')}</p>
        </div>
      </div>
    </Section>
  );
}

// "for-golf-teachers" → "forGolfTeachers" — matches the common:seo.X key shape.
function camelCase(s: string): string {
  return s.replace(/-([a-z])/g, (_, c) => c.toUpperCase());
}
